import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Award, RefreshCcw, Building2, Check, X, Infinity, Database
} from 'lucide-react';

const SubscriptionsPage = ({ onMenuClick }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await api.get('/super-admin/plans');
      setPlans(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const getFeatureIcon = (enabled) => {
    return enabled ? (
      <Check className="w-4 h-4 text-emerald-600" />
    ) : (
      <X className="w-4 h-4 text-slate-300" />
    );
  };

  const formatLimit = (value) => {
    if (value === null || value === undefined) {
      return <Infinity className="w-4 h-4 text-emerald-600" />;
    }
    return value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCcw className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="subscriptions-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onMenuClick} className="md:hidden p-2 -ml-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800" aria-label="Ouvrir le menu">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Plans d'abonnement
            </h1>
            <p className="text-slate-500">Gestion des offres OFPilot</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchPlans}>
          <RefreshCcw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((plan, idx) => {
          const isPopular = plan.type === 'medium';
          return (
            <Card 
              key={plan.id}
              className={`relative ${isPopular ? 'border-2 border-violet-500 shadow-lg' : ''}`}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-violet-600 text-white">
                    Populaire
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-2">
                <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-4 ${
                  idx === 0 ? 'bg-slate-100' : idx === 1 ? 'bg-violet-100' : 'bg-amber-100'
                }`}>
                  <Award className={`w-6 h-6 ${
                    idx === 0 ? 'text-slate-600' : idx === 1 ? 'text-violet-600' : 'text-amber-600'
                  }`} />
                </div>
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div className="text-center">
                  <p className="text-4xl font-bold text-slate-900 dark:text-white">
                    {plan.price_monthly}€
                  </p>
                  <p className="text-slate-500">/ mois</p>
                  <p className="text-sm text-slate-400 mt-1">
                    ou {plan.price_yearly}€/an
                  </p>
                </div>

                <div className="space-y-3">
                  <p className="font-semibold text-sm text-slate-700 dark:text-slate-300">Limites</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <span>Étudiants</span>
                      <span className="font-semibold">{formatLimit(plan.limits.max_students)}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <span>Formateurs</span>
                      <span className="font-semibold">{formatLimit(plan.limits.max_trainers)}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <span>Cours</span>
                      <span className="font-semibold">{formatLimit(plan.limits.max_courses)}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <span>Stockage</span>
                      <span className="font-semibold">{plan.limits.max_storage_gb} Go</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <span>Admins</span>
                      <span className="font-semibold">{formatLimit(plan.limits.max_admins)}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="font-semibold text-sm text-slate-700 dark:text-slate-300">Fonctionnalités</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between p-2">
                      <span>Sessions Live</span>
                      {getFeatureIcon(plan.features.live_sessions)}
                    </div>
                    <div className="flex items-center justify-between p-2">
                      <span>Assistant IA</span>
                      {getFeatureIcon(plan.features.ai_assistant)}
                    </div>
                    <div className="flex items-center justify-between p-2">
                      <span>Analytics avancées</span>
                      {getFeatureIcon(plan.features.advanced_analytics)}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-slate-500">
                      <Building2 className="w-4 h-4" />
                      Centres actifs
                    </span>
                    <Badge variant="secondary">{plan.centers_count}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Database className="w-6 h-6 text-slate-400 mt-1" />
            <div>
              <p className="font-semibold text-slate-900 dark:text-white">
                Gestion des limites
              </p>
              <p className="text-sm text-slate-500 mt-1">
                Les limites définies dans chaque plan sont enregistrées en base de données 
                mais ne sont pas encore appliquées automatiquement. Cette fonctionnalité 
                sera activée dans une prochaine version pour bloquer les dépassements.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionsPage;
